import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';

import { useGridUserDataGlobalFiltersContext } from '../../../contexts/GridUserDataGlobalFiltersProvider';
import { useClearUserDataOnTerminalChange } from "../hooks/useClearUserDataOnTerminalChange";
import { ReadyConfig } from "../shared/types";
import { ConfigFileStatusType } from "../../../entities/ConfigFileStatus";

export interface ConfigFile {
  src: string;
  name: string;
  created_at: string;
  updated_at: string;
  terminal_id: number;
  status: ConfigFileStatusType;
  preview_src?: string;
}

// Config state managed by reducer
interface ConfigState {
  readyConfig: ReadyConfig | null;
  currentConfigSrc: string | null;
  configFileStatus: ConfigFileStatusType | null;
  readyConfigHistory: ReadyConfig[];
  currentReadyHistoryIndex: number;
}

type ConfigAction =
  | { type: 'SET_CONFIG'; payload: { config: ReadyConfig; status: ConfigFileStatusType | null; src?: string | null } }
  | { type: 'CLEAR_CONFIG' }
  | { type: 'SET_READY_CONFIG'; payload: ReadyConfig | null }
  | { type: 'SET_CURRENT_CONFIG_SRC'; payload: string | null }
  | { type: 'SET_CONFIG_FILE_STATUS'; payload: ConfigFileStatusType | null }
  | { type: 'SET_READY_CONFIG_HISTORY'; payload: ReadyConfig[] }
  | { type: 'SET_CURRENT_READY_HISTORY_INDEX'; payload: number };

const initialConfigState: ConfigState = {
  readyConfig: null,
  currentConfigSrc: null,
  configFileStatus: null,
  readyConfigHistory: [],
  currentReadyHistoryIndex: -1,
};

function configReducer(state: ConfigState, action: ConfigAction): ConfigState {
  switch (action.type) {
    case 'SET_CONFIG':
      // Batch update all config-related state in one render
      return {
        ...state,
        readyConfig: action.payload.config,
        readyConfigHistory: [action.payload.config],
        currentReadyHistoryIndex: 0,
        configFileStatus: action.payload.status,
        currentConfigSrc: action.payload.src ?? state.currentConfigSrc,
      };
    case 'CLEAR_CONFIG':
      return initialConfigState;
    case 'SET_READY_CONFIG':
      return { ...state, readyConfig: action.payload };
    case 'SET_CURRENT_CONFIG_SRC':
      return { ...state, currentConfigSrc: action.payload };
    case 'SET_CONFIG_FILE_STATUS':
      return { ...state, configFileStatus: action.payload };
    case 'SET_READY_CONFIG_HISTORY':
      return { ...state, readyConfigHistory: action.payload };
    case 'SET_CURRENT_READY_HISTORY_INDEX':
      return { ...state, currentReadyHistoryIndex: action.payload };
    default:
      return state;
  }
}

interface ITerminalContext {
  // Config state
  readyConfig: ReadyConfig | null;
  setReadyConfig: (readyConfig: ReadyConfig | null) => void;
  currentConfigSrc: string | null;
  setCurrentConfigSrc: (src: string | null) => void;
  configFileStatus: ConfigFileStatusType | null;
  setConfigFileStatus: (status: ConfigFileStatusType | null) => void;

  // History for undo/redo
  readyConfigHistory: ReadyConfig[];
  setReadyConfigHistory: (history: ReadyConfig[]) => void;
  currentReadyHistoryIndex: number;
  setCurrentReadyHistoryIndex: (index: number) => void;

  // Batch actions
  setConfigState: (config: ReadyConfig, status: ConfigFileStatusType | null, src?: string | null) => void;
  clearConfigState: () => void;

  // File list for the selected terminal
  fileList: ConfigFile[];
  setFileList: (files: ConfigFile[]) => void;

  // UI state
  error: string | null;
  setError: (error: string | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const TerminalContext = createContext<ITerminalContext | undefined>(undefined);

export const TerminalProvider = ({ children }: { children: ReactNode }) => {
  const { data: globalTerminalId = {} } = useGridUserDataGlobalFiltersContext();

  // Use reducer for config-related state to batch updates
  const [configState, dispatchConfig] = useReducer(configReducer, initialConfigState);

  const [fileList, setFileList] = useState<ConfigFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Individual setters for backward compatibility
  const setReadyConfig = useCallback((config: ReadyConfig | null) => {
    dispatchConfig({ type: 'SET_READY_CONFIG', payload: config });
  }, []);

  const setCurrentConfigSrc = useCallback((src: string | null) => {
    dispatchConfig({ type: 'SET_CURRENT_CONFIG_SRC', payload: src });
  }, []);

  const setConfigFileStatus = useCallback((status: ConfigFileStatusType | null) => {
    dispatchConfig({ type: 'SET_CONFIG_FILE_STATUS', payload: status });
  }, []);

  const setReadyConfigHistory = useCallback((history: ReadyConfig[]) => {
    dispatchConfig({ type: 'SET_READY_CONFIG_HISTORY', payload: history });
  }, []);

  const setCurrentReadyHistoryIndex = useCallback((index: number) => {
    dispatchConfig({ type: 'SET_CURRENT_READY_HISTORY_INDEX', payload: index });
  }, []);

  // Batch actions - wrapped in unstable_batchedUpdates for React 16 compatibility
  // In React 18+ this is automatic, but doesn't hurt to keep it
  const setConfigState = useCallback((config: ReadyConfig, status: ConfigFileStatusType | null, src?: string | null) => {
    unstable_batchedUpdates(() => {
      dispatchConfig({ type: 'SET_CONFIG', payload: { config, status, src } });
    });
  }, []);

  const clearConfigState = useCallback(() => {
    unstable_batchedUpdates(() => {
      dispatchConfig({ type: 'CLEAR_CONFIG' });
    });
  }, []);

  useClearUserDataOnTerminalChange(
    globalTerminalId?.terminal_id?.[0],
    setReadyConfig,
    setError,
    setFileList,
    setReadyConfigHistory,
    setCurrentReadyHistoryIndex,
    setCurrentConfigSrc
  );

  const contextValue = useMemo(() => ({
    readyConfig: configState.readyConfig,
    setReadyConfig,
    currentConfigSrc: configState.currentConfigSrc,
    setCurrentConfigSrc,
    configFileStatus: configState.configFileStatus,
    setConfigFileStatus,
    readyConfigHistory: configState.readyConfigHistory,
    setReadyConfigHistory,
    currentReadyHistoryIndex: configState.currentReadyHistoryIndex,
    setCurrentReadyHistoryIndex,
    setConfigState,
    clearConfigState,
    fileList,
    setFileList,
    error,
    setError,
    isLoading,
    setIsLoading,
  }), [
    configState,
    setReadyConfig,
    setCurrentConfigSrc,
    setConfigFileStatus,
    setReadyConfigHistory,
    setCurrentReadyHistoryIndex,
    setConfigState,
    clearConfigState,
    fileList,
    error,
    isLoading,
  ]);

  return <TerminalContext.Provider value={contextValue}>{children}</TerminalContext.Provider>;
};

export const useTerminalContext = () => {
  const context = useContext(TerminalContext);
  if (context === undefined) {
    throw new Error("useTerminalContext must be used within a TerminalProvider");
  }
  return context;
};
